import { useState } from 'react';
import { cva } from 'class-variance-authority';
import { reportCommandError } from '@/lib/recordings/report-command-error';

import { commands } from '@/lib/platform/generated/bindings.gen';
import { Flex, Spin, Divider } from '@nula/core-ui';
import { useRecordingSettings } from '../hooks/use-recording-settings';
import { useAccessibilityPermissionFlow } from '../hooks/use-accessibility-permission-flow';
import { useRecordingPermissions } from '../hooks/use-recording-permissions';
import { RetentionSection } from './settings/retention-section';
import { ShortcutCapture } from './settings/shortcut-capture';
import { DeviceSelector } from './settings/device-selector';
import { NotificationsSection } from './settings/notifications-section';
import { DetectionSourcesSection } from './settings/detection-sources-section';
import { MeetingAutomationSection } from './settings/meeting-automation-section';
import { AccessibilityExplainer } from './accessibility-explainer';
import { useIsMacOS } from '@/lib/platform/use-is-mac-os';
import styles from './recordings-settings.module.css';

export type RecordingsSettingsProps = {
  className?: string;
  /** When true, desktop-only controls are rendered but visually disabled */
  desktopOnly?: boolean;
};

const root = cva(styles.root);
const loading = cva(styles.loading);

export function RecordingsSettings({ className, desktopOnly = false }: RecordingsSettingsProps) {
  const { settings, update } = useRecordingSettings();
  // Single AX-permission hook — probes on mount, on focus, and every 5s.
  // Drives both the explainer-modal trigger flow and the inline warning
  // on the browser-detection switch in DetectionSourcesSection.
  const axFlow = useAccessibilityPermissionFlow();
  // Read-only mic + screen-recording permission probe → drives the inline
  // warnings on the device selectors when an OS permission is revoked.
  const recordingPerms = useRecordingPermissions();
  const isMacOS = useIsMacOS();
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Platform-appropriate shortcut for the web/desktopOnly placeholder.
  // isMacOS is null on first render (async probe) — treat null as macOS-unknown;
  // for the placeholder we default to Ctrl (safer: wrong on macOS is cosmetic,
  // wrong on Windows is confusing).
  const placeholderShortcut = isMacOS === true ? 'Cmd+Shift+R' : 'Ctrl+Shift+R';

  const DESKTOP_ONLY_PLACEHOLDER = {
    audioRetentionDays: 30,
    calendarNotificationsEnabled: true,
    appDetectionEnabled: true,
    shortcut: placeholderShortcut,
    micDeviceId: null,
    systemAudioDeviceId: null,
    browserDetectionEnabled: false,
    autoJoinEnabled: false,
    autoEndEnabled: false,
    endGraceSecs: 60,
  };

  // On web, Tauri is unavailable so settings never load — render a static placeholder
  const resolved = desktopOnly ? DESKTOP_ONLY_PLACEHOLDER : settings;

  // Handler for the BROWSER-detection switch only. App detection (mic-owner)
  // needs no permission and toggles directly via `update` in the component.
  // - Turning browser detection ON on macOS without AX granted: open the
  //   explainer first; only persist after the user grants (via onGranted).
  // - Otherwise (turning off, already granted, or non-macOS): persist directly.
  const handleBrowserDetectionChange = (next: boolean) => {
    if (next && isMacOS && axFlow.status !== 'granted') {
      // User wants to enable but hasn't granted AX permission yet — show the
      // explainer. The switch stays off until permission lands via onGranted.
      setExplainerOpen(true);
      return;
    }
    update({ browserDetectionEnabled: next });
  };

  // Deep-link to a Privacy pane. Used by the Accessibility browser-switch
  // warning (AX has no request command — its grant flow is the explainer's
  // AXIsProcessTrustedWithOptions). Mic/screen recovery goes through
  // handlePermissionAction (Request/Reset/Open), not this.
  const openSystemSettings = (pane: 'accessibility') => {
    void commands.openPermission(pane).then((res) => {
      if (res.status === 'error') {
        console.warn('[RecordingsSettings] open_permission failed', res.error);
      }
    });
  };

  // Explicit per-permission recovery actions for the device-selector warnings.
  // With Request as its own action, Open is now a bare deep-link (no implicit
  // request) — the user chooses Request when they want the native prompt /
  // to register Nula in the Privacy list.
  const handlePermissionAction = (
    pane: 'microphone' | 'screen_recording',
    action: 'request' | 'reset' | 'open',
  ) => {
    // Map legacy snake_case pane name to unified camelCase kind
    const kind = pane === 'microphone' ? 'microphone' : 'screenRecording';
    switch (action) {
      case 'request':
        void commands.requestPermission(kind).then((res) => {
          if (res.status === 'error') {
            console.warn('[RecordingsSettings] request_permission failed', res.error);
          }
        });
        break;
      case 'reset':
        void commands.resetPermissionKind(kind).then((res) => {
          if (res.status === 'error') {
            console.warn('[RecordingsSettings] reset_permission_kind failed', res.error);
          }
        });
        break;
      case 'open':
        void commands.openPermission(kind).then((res) => {
          if (res.status === 'error') {
            console.warn('[RecordingsSettings] open_permission failed', res.error);
          }
        });
        break;
    }
  };

  const handleRestart = () => {
    // macOS cached the pre-grant AX value in this process; only a relaunch
    // picks up the new permission. Reuses the same command as the
    // Screen-Recording restart flow.
    void commands.restartApp().then((res) => {
      if (res.status === 'error') {
        reportCommandError('restart_app', res.error);
        console.error('[RecordingsSettings] restart_app failed', res.error);
      }
    });
  };

  if (!resolved) {
    return (
      <Flex
        align="center"
        justify="center"
        data-testid="recordings-settings-loading"
        className={loading({ className })}
      >
        <Spin size="lg" />
      </Flex>
    );
  }

  return (
    <>
      <Flex vertical gap="sm" className={root({ className })}>
        <RetentionSection
          value={resolved.audioRetentionDays}
          onChange={(v) => update({ audioRetentionDays: v })}
        />

        <Divider />

        <Flex vertical gap="sm" className={desktopOnly ? styles.gatedSection : undefined}>
          <ShortcutCapture
            current={resolved.shortcut}
            onChange={(shortcut) => update({ shortcut })}
          />

          <Divider />

          <DeviceSelector
            micDeviceId={resolved.micDeviceId}
            systemDeviceId={resolved.systemAudioDeviceId}
            onChange={(micId, sysId) =>
              update({ micDeviceId: micId, systemAudioDeviceId: sysId })
            }
            micGranted={recordingPerms.micGranted}
            screenGranted={recordingPerms.screenGranted}
            onPermissionAction={handlePermissionAction}
          />

          <Divider />

          <NotificationsSection
            calendarEnabled={resolved.calendarNotificationsEnabled}
            onUpdate={update}
          />

          <Divider />

          <DetectionSourcesSection
            appDetectionEnabled={resolved.appDetectionEnabled}
            browserDetectionEnabled={resolved.browserDetectionEnabled}
            onUpdate={update}
            onBrowserToggle={handleBrowserDetectionChange}
            // Treat `null` (probe not yet resolved) as granted to avoid a
            // false-positive warning flash on initial mount.
            axGranted={axFlow.granted !== false}
            onOpenSystemSettings={() => openSystemSettings('accessibility')}
            needsRestart={axFlow.needsRestart}
            onRestart={handleRestart}
            isMacOS={isMacOS === true}
          />

          <Divider />

          {/* Auto-join / auto-end hook the native recording + presence
              pipeline, so they live in the desktop-only gated section. */}
          <MeetingAutomationSection
            autoJoinEnabled={resolved.autoJoinEnabled}
            autoEndEnabled={resolved.autoEndEnabled}
            endGraceSecs={resolved.endGraceSecs}
            onUpdate={update}
          />
        </Flex>
      </Flex>

      <AccessibilityExplainer
        open={explainerOpen}
        flow={axFlow}
        onClose={() => setExplainerOpen(false)}
        onGranted={() => {
          setExplainerOpen(false);
          // AX granted — complete the browser-detection enable the user
          // originally initiated. App detection is independent and untouched.
          update({ browserDetectionEnabled: true });
        }}
      />
    </>
  );
}
